import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Loader2 } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

export const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-semibold transition-colors active:translate-y-px focus-visible:outline-none focus-visible:shadow-glow-sm disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        primary:
          "bg-studio-accent text-studio-accent-fg shadow-sm hover:bg-studio-accent-hover",
        secondary:
          "border border-studio-border bg-studio-panel text-studio-ink hover:border-studio-border-light hover:bg-studio-card-hover",
        ghost: "text-studio-muted hover:bg-studio-card hover:text-studio-ink",
        outline:
          "border border-studio-border bg-transparent text-studio-muted hover:border-studio-border-light hover:text-studio-ink",
        danger: "bg-studio-danger/90 text-studio-danger-fg hover:bg-studio-danger",
      },
      size: {
        sm: "h-11 px-3 text-xs sm:h-8",
        md: "h-11 px-4 text-sm sm:h-10",
        lg: "h-12 px-6 text-sm",
        icon: "h-11 w-11 shrink-0 sm:h-9 sm:w-9",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  )
);
Button.displayName = "Button";
